import styled, { keyframes } from "styled-components";
// Animações
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Container principal
export const MainContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  min-height: 100vh;
  background: var(--app-bg-gradient);
  background-size: 400% 400%;
  font-family: "Inter", "Helvetica Neue", sans-serif;
  overflow-x: hidden;
`;

// Padrão de fundo decorativo
export const BackgroundPattern = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  opacity: 0.1;
  background-image: 
    radial-gradient(circle at 25% 25%, var(--accent-secondary) 2px, transparent 2px),
    radial-gradient(circle at 75% 75%, var(--accent) 1px, transparent 1px);
  background-size: 100px 100px, 60px 60px;
  z-index: 0;
`;

// Wrapper do conteúdo
export const ContentWrapper = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 1200px;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

// Seção do cabeçalho
export const HeaderSection = styled.header`
  text-align: center;
  margin-bottom: 4rem;
  position: relative;
`;

// Título principal
export const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  background: linear-gradient(135deg, var(--accent-secondary) 0%, var(--accent) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0 0 2rem 0;
  text-shadow: 0 4px 20px var(--accent-secondary-soft);
  text-align: center;
  animation: ${fadeInUp} 0.6s ease-out;
  animation-delay: 0.1s;
  animation-fill-mode: both;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

// Subtítulo
export const Subtitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 400;
  color: var(--text-heading);
  margin: 0;
  letter-spacing: 0.05em;
  opacity: 0.9;

  @media (max-width: 768px) {
    font-size: 1.2rem;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

// Grid de botões
export const ButtonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 3rem 4rem;
  width: 85%;
  max-width: 1200px;
  justify-content: center;

  @media (min-width: 1920px) {
    gap: 4rem 5rem;
    grid-template-columns: repeat(2, minmax(320px, 400px));
  }

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 2.5rem 3rem;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
    width: 90%;
  }

  @media (max-width: 480px) {
    gap: 1rem;
    width: 95%;
  }
`;

// Botão de ação
export const ActionButton = styled.button`
  position: relative;
  background: var(--surface-glass);
  backdrop-filter: blur(20px);
  border: 2px solid var(--border-subtle);
  border-radius: 20px;
  padding: 2.5rem;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  width: 100%;
  min-height: 220px;
  overflow: hidden;
  
  /* Pseudo-elemento para o gradiente de fundo */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, 
      var(--accent-secondary-soft) 0%, 
      var(--accent-soft) 100%
    );
    opacity: 0;
    transition: opacity 0.4s ease;
    border-radius: 20px;
    z-index: 1;
    box-shadow: inset 0 0 20px var(--accent-soft);
  }
  
  /* Pseudo-elemento para a sombra externa que se move junto */
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: 20px;
    background: transparent;
    box-shadow: 0 12px 32px var(--accent-soft);
    opacity: 0;
    transition: all 0.4s ease;
    z-index: -1;
    pointer-events: none;
  }
  
  &:hover::before {
    opacity: 1;
  }
  
  &:hover::after {
    opacity: 1;
    transform: translateY(-8px); /* Sombra se move junto com o botão */
  }
  
  &:hover {
    transform: translateY(-8px);
    border-color: var(--accent-soft);
    box-shadow: inset 0 1px 0 var(--border-subtle);
  }
  
  &:active {
    transform: translateY(-4px);
  }
  
  &:active::after {
    transform: translateY(-4px);
  }
  
  @media (min-width: 1920px) {
    min-height: 260px;
    padding: 3rem;
  }

  @media (max-width: 768px) {
    min-height: 200px;
    padding: 2rem;
  }
  
  @media (max-width: 480px) {
    min-height: 180px;
    padding: 1.5rem;
  }
  
  > * {
    position: relative;
    z-index: 2;
  }
`;

// Ícone do botão
export const ButtonIcon = styled.div`
  font-size: 2.5rem;
  margin-bottom: 1rem;
  filter: drop-shadow(0 4px 8px var(--accent-soft));

  @media (min-width: 1920px) {
    font-size: 3rem;
    margin-bottom: 1.5rem;
  }

  @media (max-width: 480px) {
    font-size: 2rem;
    margin-bottom: 0.8rem;
  }
`;

// Texto do botão
export const ButtonText = styled.h3`
  font-size: 1.15rem;
  font-weight: 600;
  color: var(--accent-secondary);
  margin: 0 0 0.75rem 0;
  line-height: 1.3;

  @media (min-width: 1920px) {
    font-size: 1.35rem;
    margin: 0 0 1rem 0;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

// Descrição do botão
export const ButtonDescription = styled.p`
  font-size: 0.9rem;
  color: var(--text-secondary);
  margin: 0;
  line-height: 1.5;
  opacity: 0.8;
  transition: opacity 0.3s ease;

  ${ActionButton}:hover & {
    opacity: 1;
    color: var(--text-heading);
  }

  @media (min-width: 1920px) {
    font-size: 1rem;
    line-height: 1.6;
  }

  @media (max-width: 480px) {
    font-size: 0.8rem;
  }
`;

// Botões legados (para manter compatibilidade)
export const StyledButton = styled(ActionButton)`
  min-height: auto;
  padding: 1rem 2rem;
  
  ${ButtonText} {
    margin: 0;
  }
`;

export const ButtonContainer = styled(ButtonGrid)`
  /* Mantido para compatibilidade */
`;

export const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 2rem;
  animation: ${fadeInUp} 0.5s ease;
`;

export const SearchInput = styled.input`
  background: var(--surface-glass);
  border: 1px solid var(--border-subtle);
  border-radius: 12px;
  padding: 0.75rem 1rem;
  width: 280px;
  color: var(--accent-secondary);
  font-size: 1rem;
  transition: all 0.3s ease;
  backdrop-filter: blur(8px);

  &:focus {
    outline: none;
    border-color: var(--accent-soft);
    box-shadow: 0 0 8px var(--accent-soft);
  }

  &::placeholder {
    color: var(--border-strong);
  }
`;

export const SearchButton = styled.button`
  background: linear-gradient(135deg, var(--accent) 0%, var(--accent-secondary) 100%);
  border: none;
  color: var(--brand-navy);
  font-weight: 600;
  border-radius: 12px;
  padding: 0.75rem 1.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px var(--accent-soft);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px var(--accent-soft);
  }

  &:active {
    transform: translateY(0);
  }
`;

export const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: var(--surface-glass);
  border-radius: 16px;
  overflow: hidden;
  animation: ${fadeInUp} 0.6s ease;

  th {
    background: var(--table-header-bg);
    color: var(--table-header-color);
    font-weight: var(--table-header-font-weight);
    font-size: var(--table-header-font-size);
    text-transform: uppercase;
    letter-spacing: var(--table-header-letter-spacing);
    padding: 0.75rem;
    text-align: left;
  }

  td {
    padding: 0.75rem;
    text-align: left;
    color: var(--text-primary);
  }

  tr:nth-child(even) {
    background-color: var(--accent-secondary-soft);
  }

  tr:nth-child(odd) {
    background-color: var(--surface-glass);
  }
`;

export const InputContainer = styled.div`
  margin-top: 2rem;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  animation: fadeInUp 0.6s ease;
`;

export const YearInput = styled.input`
  background: var(--surface-glass);
  border: 1px solid var(--accent-secondary-soft);
  border-radius: 10px;
  padding: 0.5rem 1rem;
  width: 80px;
  color: var(--accent-secondary);
  font-weight: 600;
  text-align: center;
  font-size: 1.1rem;
  backdrop-filter: blur(8px);

  &:disabled {
    opacity: 0.8;
    cursor: default;
  }
`;

export const YearButton = styled.button`
  background: linear-gradient(135deg, var(--accent), var(--accent-secondary));
  color: var(--brand-navy);
  font-weight: bold;
  border: none;
  border-radius: 10px;
  padding: 0.5rem 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px var(--accent-soft);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px var(--accent-soft);
  }

  &:active {
    transform: translateY(0);
  }
`;

export const PaginationContainer = styled.div`
  margin-top: 2rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--accent-secondary);
`;

export const PageButton = styled.button`
  background: ${(props) =>
    props.disabled
      ? "var(--accent-soft)"
      : "linear-gradient(135deg, var(--accent), var(--accent-secondary))"};
  color: var(--brand-navy);
  border: none;
  border-radius: 8px;
  padding: 0.5rem 1rem;
  cursor: ${(props) => (props.disabled ? "default" : "pointer")};
  transition: all 0.3s ease;

  &:hover {
    transform: ${(props) => (props.disabled ? "none" : "translateY(-2px)")};
  }
`;
